'use client';

import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from 'urql';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { SampleOrderDataDocument } from '@packages/gql/generated/shopping/SampleOrderDataDocument';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import type { AbortHandler } from '../types';
import { SelectSamples } from '../SampleOrderSteps/SelectSamples/SelectSamples.client';
import { SampleOrderStepper } from '../components/Stepper';
import { PersonalDataForm } from '../SampleOrderSteps/PersonalDataForm/PersonalDataForm.client';
import { Review } from '../SampleOrderSteps/Review/Review.client';
import { Finished } from '../SampleOrderSteps/Finished/Finished.client';
import type { SampleFormSchema } from '../SampleOrderSteps/schema';
import { personalDataFormDefaults, sampleFormSchema } from '../SampleOrderSteps/schema';
import { SampleOrderErrorFallback } from '../utils/SampleOrderErrorFallback/SampleOrderErrorFallback.client';
import { SampleOrderSuspenseFallback } from '../utils/SampleOrderSuspenseFallback/SampleOrderSuspenseFallback';
import { SampleOrderSteps } from '../SampleOrderSteps/types';

/* GraphQL */ `
  query SampleOrderData($locale: String!, $akl: String!) {
    sample(id: $akl, locale: $locale) {
      id
      type
      samples {
        groupName
        values {
          name
          image
          id
          kindOfPattern
        }
      }
    }
  }
`;

export type SampleOrderProps = {
  akl: string;
} & AbortHandler;

export const SampleOrder = ({ akl, abortHandler }: SampleOrderProps) => {
  const [step, setStep] = useState<SampleOrderSteps>(SampleOrderSteps.SelectSamples);
  const { locale } = useIntl();

  const { getCookies } = useCookies();
  const cookies = getCookies();
  const mode = cookies.appView === 'true' ? 'standalone' : 'default';

  const [{ data, error, fetching }] = useQuery({
    query: SampleOrderDataDocument,
    variables: { akl, locale },
  });

  const methods = useForm<SampleFormSchema>({
    resolver: zodResolver(sampleFormSchema),
    mode: 'onChange',
    defaultValues: {
      sampleData: {
        values: [],
      },
      inputData: personalDataFormDefaults,
    },
  });

  if (fetching) {
    return <SampleOrderSuspenseFallback />;
  }

  if (error || !data?.sample?.samples.length) {
    return <SampleOrderErrorFallback />;
  }

  const sampleOrderData = data.sample.samples;

  return (
    <FormProvider {...methods}>
      {/*
          Be carefull with this construct. The Sample Order has to have a dynamic height that fits the parent container without having layout (fixed height).
          This means that the Progressbar must stretch over the whole height (desktop) and the actions bar needs to be visible and accessable.
          So check the layout with only a few samples (Dialog has a small height on desktop) and with many samples (Dialog fits almoust the screen).
          Check Tablet and Mobile also!.
      */}
      <div className="flex w-full overflow-hidden">
        <div className="flex w-full flex-[1] flex-col justify-stretch overflow-hidden md:flex-row">
          {step !== SampleOrderSteps.Finished && (
            <div className="bg-color-background-base sticky md:flex-[0_0_30%]">
              <SampleOrderStepper step={step} />
            </div>
          )}
          {(() => {
            switch (step) {
              case SampleOrderSteps.SelectSamples:
                return (
                  <SelectSamples
                    abortHandler={abortHandler}
                    setStep={setStep}
                    sampleOrderData={sampleOrderData}
                    mode={mode}
                  />
                );
              case SampleOrderSteps.PersonalDataForm:
                return <PersonalDataForm setStep={setStep} />;
              case SampleOrderSteps.Review:
                return <Review setStep={setStep} sampleOrderData={sampleOrderData} />;
              case SampleOrderSteps.Finished:
                return (
                  <Finished finishSampleOrderHandler={abortHandler} setStep={setStep} mode={mode} />
                );
              /* currently the linter wants a default in a switch but with TS it is impossible to have other steps then the above */
              default:
                throw new Error('Unknown Sample Order step!');
            }
          })()}
        </div>
      </div>
    </FormProvider>
  );
};
